import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { pick } from '../../util/common';
import {
  createStripeAccount,
  updateStripeAccount,
  fetchStripeAccount,
} from '../../ducks/stripeConnectAccount.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { norwegianAccountToCompactIban } from '../../util/norwegianBankAccount';

// ================ Async thunks ================ //

const savePayoutDetailsPayloadCreator = (
  { values, isUpdateCall },
  { dispatch, extra: sdk, rejectWithValue }
) => {
  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;

  return dispatch(upsertThunk(values, { expand: true }))
    .then(response => {
      // Persist the seller's IBAN to their private profile data: on the
      // return from Stripe-hosted onboarding, the payout page reads it
      // from there and attaches the bank account via the API. Stored
      // under `privateData` (user-only-readable) because bank-account
      // identifiers shouldn't leak to transaction counterparts via
      // `protectedData`.
      const country = values?.country;
      const bankAccountNumber = values?.bankAccountNumber;
      const iban =
        country === 'NO' && bankAccountNumber
          ? norwegianAccountToCompactIban(bankAccountNumber)
          : null;
      if (!iban) {
        return response;
      }
      // Wait for the save before resolving: resolving is what sends the
      // seller to Stripe, and leaving the page cancels a request still in
      // flight. The IBAN was then missing on return, so the seller had to
      // type their account number a second time. A failed save still
      // doesn't reject the parent save; the page asks for the number then.
      return sdk.currentUser
        .updateProfile({ privateData: { stripePayoutIban: iban } })
        .catch(() => {})
        .then(() => response);
    })
    .catch(() => {
      return rejectWithValue('Failed to save payout details');
    });
};
export const savePayoutDetailsThunk = createAsyncThunk(
  'StripePayoutPage/savePayoutDetails',
  savePayoutDetailsPayloadCreator
);
// Backward compatible wrapper function
export const savePayoutDetails = (values, isUpdateCall) => dispatch => {
  return dispatch(savePayoutDetailsThunk({ values, isUpdateCall })).unwrap();
};

// Persist a corrected IBAN to privateData. Used by the payout page's
// "couldn't connect your bank account" recovery flow so a reload (and the
// automatic attach) picks up the fixed number. Best-effort: the attach
// itself uses the IBAN passed to it, so a failed persist isn't critical.
export const savePayoutIban = iban => (dispatch, getState, sdk) => {
  return sdk.currentUser.updateProfile({ privateData: { stripePayoutIban: iban } }).catch(() => {});
};

// ================ Slice ================ //

const initialState = {
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  fromReturnURL: false,
};

const stripePayoutPageSlice = createSlice({
  name: 'StripePayoutPage',
  initialState,
  reducers: {
    setInitialValues: (state, action) => {
      return { ...initialState, ...pick(action.payload, Object.keys(initialState)) };
    },
  },
  extraReducers: builder => {
    builder
      // Save Payout Details cases
      .addCase(savePayoutDetailsThunk.pending, state => {
        state.payoutDetailsSaveInProgress = true;
      })
      .addCase(savePayoutDetailsThunk.fulfilled, state => {
        state.payoutDetailsSaveInProgress = false;
        state.payoutDetailsSaved = true;
      })
      .addCase(savePayoutDetailsThunk.rejected, state => {
        state.payoutDetailsSaveInProgress = false;
      });
  },
});

// Export the action creators
export const { setInitialValues } = stripePayoutPageSlice.actions;

export default stripePayoutPageSlice.reducer;

// ================ Load Data ================ //

export const loadData = () => (dispatch, getState, sdk) => {
  // Clear state so that previously loaded data is not visible
  // in case this page load fails.
  dispatch(setInitialValues());
  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };

  return dispatch(fetchCurrentUser(fetchCurrentUserOptions)).then(response => {
    const currentUser = getState().user.currentUser;
    if (currentUser && currentUser.stripeAccount) {
      dispatch(fetchStripeAccount());
    }
    return response;
  });
};
